import type {
  FlowDesignColorPalette,
  FlowDesignTranslations,
} from '../FlowCustom/FlowCustomContent';

/** Build multipart body for PUT api/v1/flows?flowId= (Custom Flow Design). */
export function appendFlowDesignFormData(
  formData: FormData,
  flowDesign: FlowDesignForm,
  options: {
    checkOptions: string[];
    documentTypes: string[];
    dataMatch?: Record<string, unknown>;
  },
) {
  formData.append('checkOptions', JSON.stringify(options.checkOptions));
  formData.append('documentTypes', JSON.stringify(options.documentTypes));
  if (options.dataMatch && Object.keys(options.dataMatch).length > 0) {
    formData.append('dataMatch', JSON.stringify(options.dataMatch));
  }
  if (flowDesign.appBarLogoFile) {
    formData.append('appBarLogoFile', flowDesign.appBarLogoFile);
  }
  if (flowDesign.bannerImageFile) {
    formData.append('bannerImage', flowDesign.bannerImageFile);
  } else if (flowDesign.bannerRemoved) {
    formData.append('bannerImage', '');
  }
  formData.append(
    'secondaryButtonClass',
    `text-center flex m-auto text-[15px] text-[${flowDesign.secondaryButtonTextColor}]`,
  );
  formData.append(
    'documentLinkClass',
    `bg-[${flowDesign.documentLinkBgColor}] border-solid border-2 rounded-lg`,
  );
  formData.append('appBarButtonColor', flowDesign.appBarButtonColor);
  formData.append('backgroundColor', flowDesign.backgroundColor);
  formData.append('textColor', flowDesign.textColor);
  formData.append('primaryTextColor', flowDesign.primaryTextColor);
  formData.append('allowedIdsColor', flowDesign.allowedIdsColor);
  formData.append('allowedIdsTextColor', flowDesign.allowedIdsTextColor);
  formData.append('progressBarTrackColor', flowDesign.progressBarTrackColor);
  formData.append('progressBarFillColor', flowDesign.progressBarFillColor);
  if (flowDesign.color) {
    formData.append('color', JSON.stringify(flowDesign.color));
  }
  if (
    flowDesign.translations &&
    Object.keys(flowDesign.translations).length > 0
  ) {
    formData.append('translations', JSON.stringify(flowDesign.translations));
  }
}

/** Flow Design params for PUT api/v1/flows?flowId= (form-data) */
export interface FlowDesignForm {
  appBarLogo?: string;
  appBarLogoFile: File | null;
  bannerImg?: string | null;
  bannerImageFile?: File | null;
  bannerRemoved?: boolean;
  appBarClass: string;
  buttonBgColor: string;
  buttonTextColor: string;
  secondaryButtonTextColor: string;
  documentLinkBgColor: string;
  allowedIdsColor: string;
  allowedIdsTextColor: string;
  appBarButtonColor: string;
  backgroundColor: string;
  textColor: string;
  primaryTextColor: string;
  progressBarTrackColor: string;
  progressBarFillColor: string;
  color?: Partial<FlowDesignColorPalette>;
  translations?: FlowDesignTranslations;
}

const defaultThemeColor: Partial<FlowDesignColorPalette> = {
  appBarBackgroundColor: '#00387D',
  primaryBgColor: '#ffffff',
  primaryTextColor: '#000000',
  loadingSpinnerColor: '#02bd9c',
  loadingSpinnerFill: '#02bd9c',
  buttonBgColor: '#00387D',
  buttonTextColor: '#ffffff',
  progressBarBgColor: '#39f56a',
  docSelectContainerBgColor: '#ffffff',
  accentColor: '#39f56a',
  sdkSpecialTextColor: '#000000',
  incodeTextColor: '#000000',
  incodeHighlightedTextColor: '#000000',
  abortButtonColor: '#00387D',
  appBarTitleColor: '#ffffff',
  cameraHeaderBgColor: '#000000',
  tutorialAccentColor: '#000000',
  ocrFieldsBgColor: '#ffffff',
  ocrInputTextColor: '#ffffff',
  reasonActiveBgColor: '#c0f9c8',
  reasonHoverBgColor: '#c0f9c8',
  modalBgColor: '#ffffff',
  docSelectBgColor: '#00387D',
  themeSecondaryTextColor: '#00387D',
  themeErrorColor: '#c23b21',
  themeRadioColor: '#c0f9c8',
  themeRadioAccentColor: '#89379F',
  checkboxAccentColor: '#000000',
};

export const defaultFlowDesignForm: FlowDesignForm = {
  appBarLogoFile: null,
  bannerImg: null,
  bannerImageFile: null,
  bannerRemoved: false,
  appBarClass: '#00387D',
  buttonBgColor: '#00387D',
  buttonTextColor: '#ffffff',
  secondaryButtonTextColor: '#00387D',
  documentLinkBgColor: '#00387D',
  allowedIdsColor: '#FFFFFF',
  allowedIdsTextColor: '#FFFFFF',
  appBarButtonColor: '#ffffff',
  backgroundColor: '#FFFFFF',
  textColor: '#000000',
  primaryTextColor: '#000000',
  progressBarTrackColor: '#e0e0e0',
  progressBarFillColor: '#4caf50',
  translations: {
    en: {
      userConsent: {
        cancelVerification: 'Cancel verification',
      },
    },
  },
  color: { ...defaultThemeColor },
};
